/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Launch step 4 - read-only summary of everything that will be sent, with
// "Change" links back to the relevant step.

import React from 'react';
import { GPUPricing, SSHKey, Volume } from '../../../types';
import { LaunchStep, PodForm } from './types';
import { BILLING_PERIODS } from './catalog';

interface ReviewStepProps {
  form: PodForm;
  projectName?: string;
  selectedGPU: GPUPricing | null;
  includedCpu: number;
  includedMemory: number;
  imageLabel: string;
  volume: Volume | null;
  mountPath: string;
  sshKey?: SSHKey;
  onEdit: (step: LaunchStep) => void;
  onSaveTemplate: () => void;
}

const Row: React.FC<{ k: React.ReactNode; children: React.ReactNode; edit?: () => void; last?: boolean }> = ({
  k,
  children,
  edit,
  last,
}) => (
  <div
    className={`flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-6 py-3 text-sm ${
      last ? '' : 'border-b border-ps-border'
    }`}
  >
    <span className="text-ps-muted flex-none">{k}</span>
    <span className="text-ps-text sm:text-right min-w-0 break-words">
      {children}
      {edit && (
        <button type="button" className="ps-link ml-2" onClick={edit}>
          Change
        </button>
      )}
    </span>
  </div>
);

const ReviewStep: React.FC<ReviewStepProps> = ({
  form,
  projectName,
  selectedGPU,
  includedCpu,
  includedMemory,
  imageLabel,
  volume,
  mountPath,
  sshKey,
  onEdit,
  onSaveTemplate,
}) => {
  const envCount = Object.keys(form.env_vars).filter((k) => k.trim()).length;
  return (
    <section className="ps-card px-[22px] pt-2 pb-[18px]" aria-label="Configuration">
      <Row k="Pod name" edit={() => onEdit('configure')}>
        <span className="font-mono">{form.name || '-'}</span>
      </Row>
      <Row k="Project">{projectName || '-'}</Row>
      <Row k="GPU" edit={() => onEdit('gpu')}>
        {selectedGPU
          ? `${form.gpu_count} × ${selectedGPU.gpu_type} ${selectedGPU.memory_gb} GB${form.gpu_cores < 100 ? ` @ ${form.gpu_cores}%` : ''}`
          : 'None'}
      </Row>
      <Row k="Resources" edit={() => onEdit('configure')}>
        {form.cpu_cores} vCPU · {form.memory_gb} GB RAM · {form.storage_gb} GB container disk
        {selectedGPU && (
          <span className="block text-xs text-ps-subtle">
            {includedCpu} vCPU and {includedMemory} GB RAM included with the GPU
          </span>
        )}
      </Row>
      <Row k="Image" edit={() => onEdit('template')}>
        {imageLabel}
        <span className="block font-mono text-xs text-ps-subtle break-all">{form.image}</span>
      </Row>
      {form.is_private_registry && <Row k="Private registry">{form.registry_url || '-'}</Row>}
      {form.entry_point_command.trim() && (
        <Row k="Entry point">
          <span className="font-mono text-xs break-all">{form.entry_point_command}</span>
        </Row>
      )}
      <Row k="Exposed ports" edit={() => onEdit('configure')}>
        {form.ports.length ? form.ports.join(', ') : 'None'}
      </Row>
      <Row k="Environment">{envCount ? `${envCount} variable${envCount > 1 ? 's' : ''}` : 'None'}</Row>
      <Row k="NFS volume">
        {volume ? <span className="font-mono">{`${volume.name} → ${mountPath}`}</span> : 'None'}
      </Row>
      <Row k="SSH key">{sshKey ? `${sshKey.name}${sshKey.is_default ? ' (default)' : ''}` : 'No SSH access'}</Row>
      <Row k="Billing" last>
        {BILLING_PERIODS.find((b) => b.value === form.billing_period)?.label || form.billing_period}
      </Row>
      <button
        type="button"
        onClick={onSaveTemplate}
        className="mt-2 flex items-center gap-2.5 min-h-[44px] text-sm text-ps-accent hover:text-primary-300"
      >
        Save this setup as a launch template →
      </button>
    </section>
  );
};

export default ReviewStep;
